import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { LayoutDashboard, Settings, LogOut, Menu, X, Home, MessageSquare, Link2, ChevronDown, Calendar, Bell, SlidersHorizontal, CreditCard, Building2, Globe } from "lucide-react";
import { clearAuthSessionCookies, getEffectiveBotType, hasImpersonationSession } from "@/lib/authCookies";
import ImpersonationBanner from "@/components/ImpersonationBanner";

// WhatsApp icon component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Telegram icon component
const TelegramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isChatsOpen, setIsChatsOpen] = useState(false);
  const impersonating = hasImpersonationSession();
  const botType = getEffectiveBotType();

  const menuItems = [
    {
      path: "/dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
    },
    {
      path: "/dashboard/bot-settings",
      label: "Bot Settings",
      icon: SlidersHorizontal,
    },
    {
      path: "/dashboard/bot-schedule",
      label: "Bot Schedule",
      icon: Calendar,
    },
    {
      path: "/dashboard/warm-up-sequences",
      label: "Warm-up Sequences",
      icon: MessageSquare,
    },
    ...(botType === "real_estate"
      ? [
          {
            path: "/dashboard/listings",
            label: "Listings",
            icon: Home,
          },
        ]
      : []),
    {
      path: "/dashboard/account-settings",
      label: "Account Settings",
      icon: Settings,
    },
    {
      path: "/dashboard/notifications",
      label: "Notifications",
      icon: Bell,
    },
    ...(!impersonating
      ? [
          {
            path: "/dashboard/billing",
            label: "Billing",
            icon: CreditCard,
          },
        ]
      : []),
  ];

  // Chats submenu items
  const chatItems = [
    {
      path: "/dashboard/chat-test",
      label: "AI Simulator",
      icon: MessageSquare,
    },
    {
      path: "/dashboard/chat-whatsapp",
      label: "WhatsApp",
      icon: WhatsAppIcon,
    },
    {
      path: "/dashboard/chat-telegram",
      label: "Telegram",
      icon: TelegramIcon,
    },
    {
      path: "/dashboard/chat-ghl",
      label: "GoHighLevel",
      icon: Building2,
    },
    {
      path: "/dashboard/chat-web",
      label: "Website",
      icon: Globe,
    },
  ];

  // Integrations submenu
  const integrationItems = [
    {
      path: "/dashboard/integrations/messengers/whatsapp",
      label: "WhatsApp",
      icon: WhatsAppIcon,
      activeColor: "#25D366",
    },
    {
      path: "/dashboard/integrations/messengers/telegram",
      label: "Telegram",
      icon: TelegramIcon,
      activeColor: "#229ED9",
    },
    {
      path: "/dashboard/integrations/calendars/google",
      label: "Google Calendar",
      icon: Calendar,
      activeColor: "#4285F4",
    },
    {
      path: "/dashboard/integrations/crm/ghl",
      label: "GoHighLevel",
      icon: Building2,
      activeColor: "#01B47A",
    },
    {
      path: "/dashboard/integrations/web-widget",
      label: "Website Widget",
      icon: Globe,
      activeColor: "#00D1FF",
    },
  ];

  const handleLogout = () => {
    clearAuthSessionCookies();
    navigate("/");
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Check if any chat route is active
  const isChatRouteActive = chatItems.some((item) => isActive(item.path));

  // Auto-expand Chats submenu when a chat route is active
  useEffect(() => {
    if (isChatRouteActive) {
      setIsChatsOpen(true);
    }
  }, [isChatRouteActive]);

  // Close sidebar when route changes (mobile)
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <>
      {/* Mobile Menu Button - Only visible on XS screens */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed top-4 left-4 z-[60] md:hidden flex items-center justify-center w-10 h-10 rounded-lg bg-[#0F1115] border border-white/10 text-[#F3F4F6] hover:bg-white/5 hover:border-[#00D1FF]/30 transition-all"
        data-testid="sidebar-mobile-toggle"
        aria-label="Open menu"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Overlay - Only visible on mobile when sidebar is open - covers area to the right of sidebar */}
      {isOpen && (
        <div
          className="fixed inset-y-0 left-64 right-0 bg-black/60 backdrop-blur-sm z-[65] md:hidden"
          onClick={() => setIsOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Close button - Positioned outside sidebar for proper z-index */}
      {isOpen && (
        <button
          onClick={() => setIsOpen(false)}
          className="fixed top-6 left-[210px] z-[70] md:hidden flex items-center justify-center w-8 h-8 rounded-lg text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-white/5 transition-all"
          data-testid="sidebar-close-button"
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full w-64 bg-[#0F1115] border-r border-white/5 flex flex-col z-[65] transition-transform duration-300 ease-in-out md:translate-x-0 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        {impersonating && <ImpersonationBanner />}
        {/* Logo */}
        <div className="p-6 border-b border-white/5 flex items-center justify-between">
          <Link to="/">
            <img
              src="/images/logo.png"
              alt="FractoLabs"
              className="h-6 md:h-7 w-auto"
              data-testid="sidebar-logo"
            />
          </Link>
          {/* Spacer for visual balance where close button would be */}
          <div className="w-8 h-8 md:hidden" />
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all font-body text-sm ${
                      active
                        ? "bg-[#00D1FF]/10 text-[#00D1FF] border border-[#00D1FF]/30"
                        : "text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-white/5"
                    }`}
                    data-testid={`sidebar-link-${item.path.replace(/\//g, "-")}`}
                  >
                    <Icon className={`w-5 h-5 ${active ? "text-[#00D1FF]" : ""}`} />
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>

          {/* Chats Section */}
          <div className="mt-2">
            <button
              onClick={() => setIsChatsOpen(!isChatsOpen)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all font-body text-sm ${
                isChatRouteActive
                  ? "text-[#00D1FF]"
                  : "text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-white/5"
              }`}
              data-testid="sidebar-chats-toggle"
            >
              <div className="flex items-center gap-3">
                <MessageSquare className={`w-5 h-5 ${isChatRouteActive ? "text-[#00D1FF]" : ""}`} />
                Chats
              </div>
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${
                  isChatsOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            <div
              className={`overflow-hidden transition-all duration-200 ${
                isChatsOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <ul className="space-y-1 mt-1 ml-4 pl-3 border-l border-white/10">
                {chatItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all font-body text-sm ${
                          active
                            ? "bg-[#00D1FF]/10 text-[#00D1FF] border border-[#00D1FF]/30"
                            : "text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-white/5"
                        }`}
                        data-testid={`sidebar-link-${item.path.replace(/\//g, "-")}`}
                      >
                        <Icon className={`w-4 h-4 ${active ? "text-[#00D1FF]" : ""}`} />
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Integrations Section */}
          <div className="mt-6 pt-4 border-t border-white/5">
            <div className="flex items-center gap-2 px-4 py-2 text-[#9CA3AF] text-xs font-body uppercase tracking-wider">
              <Link2 className="w-4 h-4" />
              Integrations
            </div>
            <ul className="space-y-1 mt-2">
              {integrationItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                const color = item.activeColor || "#25D366";
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all font-body text-sm ${
                        active
                          ? "border"
                          : "text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-white/5"
                      }`}
                      style={active ? { backgroundColor: `${color}1A`, color: color, borderColor: `${color}4D` } : {}}
                      data-testid={`sidebar-link-${item.path.replace(/\//g, "-")}`}
                    >
                      <Icon className="w-4 h-4" style={active ? { color: color } : {}} />
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>

        {/* Logout Button */}
        {!impersonating && (
        <div className="p-4 border-t border-white/5">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg w-full text-[#9CA3AF] hover:text-red-400 hover:bg-red-500/10 transition-all font-body text-sm"
            data-testid="sidebar-logout-button"
          >
            <LogOut className="w-5 h-5" />
            Log Out
          </button>
        </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;
